import React, { type ReactElement, type ReactNode } from "react";
import {
  ChevronRight,
  LayoutGrid,
  MessageSquare,
  MessagesSquare,
  PanelLeft,
  Users,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@ndd/ui";
import { avatarClass, initials } from "../../lib/avatar";
import { Eyebrow } from "./eyebrow";
import type { RailLinkIcon, RailModel, RailRd, RailRow } from "../../lib/rail-sections";
import {
  SiderailIcon,
  SiderailLabelClip,
  SiderailSoonItem,
  SiderailTooltip,
} from "./siderail-parts";

// The single rail's presentational parts (NO-754): section headings, link and
// RD rows, and the head. The account row at the rail's foot is its own leaf
// (rail-account-parts.tsx). A leaf module (`import React`, relative imports,
// no router, no queries) so `tsx --test` renders it; the composer
// (always-rail.tsx) supplies real links through `renderLink`, the
// Appointments entry through `renderAppointments`, the Notifications row with
// its unread count through `renderNotifications`, and an RD row with its
// chats through `renderRd`.

export type RailHref =
  | { to: "/rds" }
  | { to: "/notifications" }
  | { to: "/patients" }
  | { to: "/messages" }
  | { to: "/rds/$rdId"; rdId: string }
  | { to: "/rds/$rdId/chats/new"; rdId: string }
  | { to: "/rds/$rdId/chats/$chatId"; rdId: string; chatId: string };

export interface RailLinkProps {
  href: RailHref;
  current: boolean;
  className: string;
  children: ReactNode;
}

export type RenderRailLink = (props: RailLinkProps) => ReactElement;

const LINK_ICONS: Record<RailLinkIcon, LucideIcon> = {
  team: UsersRound,
  "all-rds": LayoutGrid,
  patients: Users,
  messages: MessagesSquare,
};

const SOON_ICONS: Record<"patients" | "chat", LucideIcon> = {
  patients: Users,
  chat: MessageSquare,
};

// Under the always theme (D1, restyle only) the current row is card-white with
// a 1.5px olive inset and weight 600, rows are 12px-round Inter, and the rail
// sits on cream. These leaf parts take no className, so brand: utilities
// carry the theme.
const ROW_TONE = {
  current:
    "bg-accent font-bold text-foreground brand:font-semibold brand:bg-card brand:shadow-[inset_0_0_0_1.5px_var(--primary)]",
  // A row whose nested row is open (an RD's chat, an Appointment in Today):
  // named, but the nested row carries the highlight.
  parent: "font-bold text-foreground hover:bg-accent",
  none: "font-medium text-muted-foreground hover:bg-accent hover:text-foreground",
} as const;

function rowClass(current: boolean, parent = false): string {
  const tone = current ? "current" : parent ? "parent" : "none";

  return cn(
    "flex h-10 items-center rounded-lg font-sans text-body-lg transition-colors brand:rounded-md brand:font-body",
    ROW_TONE[tone]
  );
}

/**
 * An RD row's classes. A parent row in the icon strip, where its chats are
 * hidden, keeps the highlight on desktop; the drawer below lg shows the chats.
 */
function rdRowClass(current: boolean, parent: boolean, collapsed: boolean): string {
  return cn(rowClass(current, parent), parent && collapsed && "lg:bg-accent");
}

export type RailRowTone = keyof typeof ROW_TONE;

/**
 * A row's classes by tone, for an entry that marks itself: the Appointments
 * row goes "parent" while its open Appointment's row carries the highlight.
 */
export function railRowClass(tone: RailRowTone): string {
  return rowClass(tone === "current", tone === "parent");
}

// In the icon strip a heading would clip to nothing, so a hairline stands in.
export function RailSectionHeading({
  label,
  collapsed,
}: {
  label: string;
  collapsed: boolean;
}): ReactElement {
  return (
    <div aria-hidden="true" className="relative flex h-8 items-end px-2.5 pb-1.5">
      <Eyebrow
        size="sm"
        className={cn(
          "whitespace-nowrap transition-opacity duration-150",
          collapsed && "lg:opacity-0"
        )}
      >
        {label}
      </Eyebrow>
      <span
        className={cn(
          "absolute inset-x-3 bottom-3 border-t border-(color:--border-decorative,var(--border-strong)) opacity-0 transition-opacity duration-150",
          collapsed && "lg:opacity-100"
        )}
      />
    </div>
  );
}

export function RailLinkRow({
  label,
  icon,
  href,
  current,
  collapsed,
  renderLink,
}: {
  label: string;
  icon: RailLinkIcon;
  href: RailHref;
  current: boolean;
  collapsed: boolean;
  renderLink: RenderRailLink;
}): ReactElement {
  return (
    <SiderailTooltip label={label} collapsed={collapsed}>
      {renderLink({
        href,
        current,
        className: rowClass(current),
        children: (
          <>
            <SiderailIcon icon={LINK_ICONS[icon]} />
            <SiderailLabelClip collapsed={collapsed}>{label}</SiderailLabelClip>
          </>
        ),
      })}
    </SiderailTooltip>
  );
}

export function RailAvatar({ rd }: { rd: RailRd }): ReactElement {
  return (
    <span className="grid size-10 shrink-0 place-items-center">
      <span
        aria-hidden="true"
        className={cn(
          "grid size-6 place-items-center rounded-full font-sans text-micro font-bold text-white",
          avatarClass(rd.id)
        )}
      >
        {initials(rd.name)}
      </span>
    </span>
  );
}

/** An RD row's chevron: whether its chats are showing, and how to flip that. */
export interface RailRdDisclosureProps {
  open: boolean;
  onToggle: () => void;
}

// The icon strip shows no chats, so its RD rows show no chevron either; the
// drawer below lg is always the expanded layout and keeps both.
function RailRdChevron({
  rdName,
  open,
  onToggle,
  collapsed,
}: RailRdDisclosureProps & { rdName: string; collapsed: boolean }): ReactElement {
  return (
    <RailRowChevron
      label={`${open ? "Hide" : "Show"} chats about ${rdName}`}
      open={open}
      onToggle={onToggle}
      collapsed={collapsed}
    />
  );
}

export function RailRdRow({
  rd,
  current,
  parent = false,
  collapsed,
  renderLink,
  disclosure,
}: {
  rd: RailRd;
  current: boolean;
  /** Its open chat's row carries the highlight; the RD row only names it. */
  parent?: boolean;
  collapsed: boolean;
  renderLink: RenderRailLink;
  disclosure?: RailRdDisclosureProps;
}): ReactElement {
  const link = (
    <SiderailTooltip label={rd.name} collapsed={collapsed}>
      {renderLink({
        href: { to: "/rds/$rdId", rdId: rd.slug },
        current,
        className: rdRowClass(current, parent, collapsed),
        children: (
          <>
            <RailAvatar rd={rd} />
            <SiderailLabelClip collapsed={collapsed}>
              <span className={cn("block truncate", disclosure ? "pr-9" : "pr-2")}>
                {rd.name}
              </span>
            </SiderailLabelClip>
          </>
        ),
      })}
    </SiderailTooltip>
  );
  if (!disclosure) return link;

  return (
    <div className="relative">
      {link}
      <RailRdChevron rdName={rd.name} collapsed={collapsed} {...disclosure} />
    </div>
  );
}

/**
 * A row's disclosure chevron at its right end, its own button beside the
 * row's link. The icon strip hides it (nothing nests there); the drawer below
 * lg is always the expanded layout and keeps it.
 */
export function RailRowChevron({
  label,
  open,
  onToggle,
  collapsed,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  collapsed: boolean;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-label={label}
      className={cn(
        "absolute right-1 top-1.5 grid size-7 place-items-center rounded-md text-sand-500 outline-none transition-colors hover:bg-sand-300/60 hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring",
        collapsed && "lg:hidden"
      )}
    >
      <ChevronRight
        aria-hidden="true"
        className={cn("size-4 transition-transform", open && "rotate-90")}
      />
    </button>
  );
}

// "Recently viewed" sits inside the RDs section, a step quieter than a heading.
export function RailSubheading({
  label,
  collapsed,
}: {
  label: string;
  collapsed: boolean;
}): ReactElement {
  return (
    <p
      className={cn(
        "whitespace-nowrap px-2.5 pb-1 pt-2.5 text-meta font-medium text-subtle transition-opacity duration-150",
        collapsed && "lg:opacity-0"
      )}
    >
      {label}
    </p>
  );
}

export type RailRdRowModel = Extract<RailRow, { kind: "rd" }>;

export interface RailRowContext {
  activeKey: string | null;
  collapsed: boolean;
  renderLink: RenderRailLink;
  renderAppointments: (key: string) => ReactNode;
  renderNotifications: (key: string, current: boolean) => ReactNode;
  /** An RD row with its chats; without it, a plain RD row. */
  renderRd?: (row: RailRdRowModel, current: boolean) => ReactNode;
}

function renderRow(row: RailRow, context: RailRowContext): ReactNode {
  switch (row.kind) {
    case "appointments":
      return context.renderAppointments(row.key);
    case "notifications":
      return context.renderNotifications(row.key, context.activeKey === row.key);
    case "soon":
      return (
        <SiderailSoonItem
          icon={SOON_ICONS[row.icon]}
          label={row.label}
          description={row.description}
          collapsed={context.collapsed}
        />
      );
    case "link":
      return (
        <RailLinkRow
          label={row.label}
          icon={row.icon}
          href={{ to: row.to }}
          current={context.activeKey === row.key}
          collapsed={context.collapsed}
          renderLink={context.renderLink}
        />
      );
    case "heading":
      return <RailSubheading label={row.label} collapsed={context.collapsed} />;
    case "rd":
      return context.renderRd ? (
        context.renderRd(row, context.activeKey === row.key)
      ) : (
        <RailRdRow
          rd={row.rd}
          current={context.activeKey === row.key}
          collapsed={context.collapsed}
          renderLink={context.renderLink}
        />
      );
    default:
      return assertNever(row);
  }
}

function assertNever(row: never): never {
  throw new Error(`Unhandled rail row: ${JSON.stringify(row)}`);
}

export function RailRowView({
  row,
  context,
}: {
  row: RailRow;
  context: RailRowContext;
}): ReactElement {
  return <>{renderRow(row, context)}</>;
}

/** Every section in order, each a labelled group of rows. */
export function RailSectionList({
  model,
  context,
}: {
  model: RailModel;
  context: RailRowContext;
}): ReactElement {
  return (
    <>
      {model.sections.map((section) => (
        <div key={section.key} role="group" aria-label={section.label}>
          <RailSectionHeading label={section.label} collapsed={context.collapsed} />
          <div className="flex flex-col gap-0.5">
            {section.rows.map((row) => (
              <RailRowView key={row.key} row={row} context={context} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

/**
 * The Always AI mark: the NDD heart, drawn in the current text color so the
 * always theme recolors it with the wordmark (prototype `img/heart.svg`).
 */
export function AlwaysHeart({ className }: { className?: string }): ReactElement {
  return (
    <svg
      viewBox="471.38 620.13 101.87 100.25"
      aria-hidden="true"
      focusable="false"
      className={cn("shrink-0 fill-current", className)}
    >
      <path
        transform="matrix(1,0,0,-1,486.6545,687.9536)"
        d="M0 0H14.583V33.596H19.161V0H33.745 40.486 47.016V6.53 36.657C47.016 37.663 46.965 38.658 46.866 39.639 46.852 39.77 46.831 39.898 46.816 40.028 45.144 54.547 32.819 65.825 17.848 65.825H15.897C.926 65.825-11.399 54.547-13.071 40.028-13.086 39.898-13.108 39.77-13.121 39.639-13.22 38.658-13.272 37.663-13.272 36.657V6.53 0H-6.741Z"
      />
      <path
        transform="matrix(1,0,0,-1,542.0783,654.3573)"
        d="M0 0H-3.829V-19.959-27.154-33.684H2.701 23.482 29.169V-29.169C29.169-13.059 16.109 0 0 0"
      />
      <path
        transform="matrix(1,0,0,-1,473.3822,695.9289)"
        d="M0 0V-15.923-22.453H6.53 27.311 73.766C86.99-22.453 97.711-11.733 97.711 1.491V3.9H0Z"
      />
    </svg>
  );
}

/**
 * The heart and "Always AI", as the rail head and the mobile bar show them. The
 * heart sits centred in the rail's 40px icon slot, where the collapsed rail's
 * expand button puts it too, so it holds still as the rail collapses.
 */
export function AlwaysWordmark(): ReactElement {
  return (
    <span className="flex items-center">
      <span className="grid size-10 shrink-0 place-items-center">
        <AlwaysHeart className="size-[26px]" />
      </span>
      <span className="ml-1 font-sans text-lg font-bold tracking-tight">Always AI</span>
    </span>
  );
}

/**
 * The desktop density toggle is the whole head row: heart, wordmark and the
 * space after them. The panel icon at the row's right end shows only while the
 * row is hovered or focused. Collapsed, the heart is all that is left of the
 * row, and it turns into the panel icon on hover or focus, which says what a
 * click does. The heart keeps one 40px slot in both states, so it holds still.
 * Below lg the drawer is always expanded and its wordmark links home instead.
 */
export function RailHeadToggle({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}): ReactElement {
  const label = collapsed ? "Expand navigation" : "Collapse navigation";

  return (
    <SiderailTooltip label={label} collapsed={collapsed}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-label={label}
        className="group hidden h-10 min-w-0 flex-1 items-center rounded-md text-left text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring lg:flex"
      >
        <span className="grid size-10 shrink-0 place-items-center">
          <AlwaysHeart
            className={cn(
              "size-[26px]",
              collapsed && "group-hover:hidden group-focus-visible:hidden"
            )}
          />
          {collapsed ? (
            <PanelLeft className="hidden size-4 text-muted-foreground group-hover:block group-focus-visible:block" />
          ) : null}
        </span>
        <SiderailLabelClip collapsed={collapsed}>
          <span className="flex w-full items-center">
            <span className="ml-1 font-sans text-lg font-bold tracking-tight">
              Always AI
            </span>
            <PanelLeft className="ml-auto mr-3 size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
          </span>
        </SiderailLabelClip>
      </button>
    </SiderailTooltip>
  );
}
